// @flow

import React, { Component, Fragment } from "react";
import { map, orderBy, filter, some, find } from "lodash";
import type {
  RaceTypeCodeEnum,
  RacePool,
  BettingInterestPool,
  BettingInterestsPools
} from "@tvg/types/Race";
import { formatCurrency } from "@tvg/formatter";

import MessageBox from "../../_molecule/MessageBox";
import RunnerNumber from "../../_atom/RunnerNumber";
import { Table, Th, Td, Runner } from "./styled-components";

type Props = {
  /**
   * Race Pools object
   */
  racePools: Array<RacePool> | null,
  /**
   * Runners Pools object
   */
  runnersPools: Array<BettingInterestsPools> | null,
  /**
   * Race type code
   */
  raceTypeCode: RaceTypeCodeEnum,
  /**
   * WagerType from where we will present pools
   */
  wagerTypeLimit: number
};

const getOrderedRacePools = (
  racePools: Array<RacePool> | null,
  wagerTypeLimit: number
) =>
  orderBy(
    filter(racePools, (pool: RacePool) => pool.wagerType.id <= wagerTypeLimit),
    ["wagerType.id"],
    ["asc"]
  );

const getPools = (
  racePools: Array<RacePool> | null,
  biPools: Array<BettingInterestPool> | null,
  wagerTypeLimit: number
) => {
  const racePoolsOrdered = getOrderedRacePools(racePools, wagerTypeLimit);

  return map(racePoolsOrdered, (racePool: RacePool) => {
    const pool = find(
      biPools,
      (biPool) => biPool.wagerType.id === racePool.wagerType.id
    );

    return (
      pool || {
        wagerType: racePool.wagerType,
        poolRunnersData: []
      }
    );
  });
};

export default class RacePools extends Component<Props> {
  static defaultProps = {
    racePools: null,
    runnersPools: null,
    raceTypeCode: "T",
    wagerTypeLimit: 70
  };

  renderWagerTypesPools() {
    const racePools = getOrderedRacePools(
      this.props.racePools,
      this.props.wagerTypeLimit
    );

    return (
      <thead>
        <tr>
          <Th key="pools-column-biNumber" first>
            <strong>#</strong>
          </Th>

          {map(racePools, (pool: RacePool) => (
            <Th key={`pools-column-${pool.wagerType.code}`}>
              <strong>{pool.wagerType.name}</strong>
              {formatCurrency(pool.amount, "USD", 0)}
            </Th>
          ))}
        </tr>
      </thead>
    );
  }

  renderRunnersPools() {
    return (
      <tbody>
        {map(this.props.runnersPools, (bi: BettingInterestsPools) => {
          const biNotScratched = some(
            bi.runners,
            (runner: { runnerId: string, scratched: boolean }) =>
              !runner.scratched
          );

          const biPools = getPools(
            this.props.racePools,
            bi.biPools,
            this.props.wagerTypeLimit
          );

          return (
            <tr key={`pools-line-biNumber-${bi.biNumber}`}>
              <Td key={`pools-column-biNumber-${bi.biNumber}`} first>
                <Runner>
                  <RunnerNumber
                    raceTypeCode={this.props.raceTypeCode}
                    runnerId={bi.runners[0].runnerId}
                  />
                </Runner>
              </Td>
              {map(
                biPools,
                (pool: BettingInterestPool) =>
                  pool.wagerType.id <= this.props.wagerTypeLimit && (
                    <Td
                      key={`pools-column-${pool.wagerType.code}-biNumber-${bi.biNumber}`}
                    >
                      {biNotScratched && pool.poolRunnersData.length
                        ? formatCurrency(
                            pool.poolRunnersData[0].amount,
                            "USD",
                            0
                          )
                        : "-"}
                    </Td>
                  )
              )}
            </tr>
          );
        })}
      </tbody>
    );
  }

  render() {
    if (this.props.racePools) {
      return (
        <Table>
          {this.renderWagerTypesPools()}
          {this.renderRunnersPools()}
        </Table>
      );
    }

    return (
      <MessageBox
        type="info"
        message={
          <Fragment>
            Pools information isn't available yet
            <br />
            Please check back later
          </Fragment>
        }
        title="Pools Unavailable"
        isVertical
      />
    );
  }
}
